/**
 * People & Teams Help Content
 *
 * Covers team browsing, membership management, and join requests.
 */

import type { HelpContent } from '../types';
import {
  createHelpContent,
  createSection,
  createFAQ,
  createTip,
  createRelatedLink,
} from '../utils';

// =============================================================================
// MAIN PEOPLE & TEAMS HELP
// =============================================================================

export const peopleTeamsHelp: HelpContent = createHelpContent({
  id: 'people-teams',
  title: 'People & Teams',
  description: 'Manage teams, team membership, and join requests',
  lastUpdated: '2026-02-08',
  version: '1.0',

  tips: [
    createTip(
      'tip-multiple-teams',
      'info',
      'Multiple Teams',
      'Employees can be members of multiple teams. Your primary team is set by your admin or HR, but you can join additional teams.'
    ),
  ],

  sections: [
    createSection(
      'overview',
      'Overview',
      `<p>The People & Teams page helps you browse the organizational structure, manage team memberships, and handle join requests.</p>
      <p>Key features:</p>
      <ul>
        <li><strong>Teams Tab</strong> — Browse all teams and their members</li>
        <li><strong>People Tab</strong> — View all employees and their team assignments</li>
        <li><strong>Join Requests</strong> — Request to join teams or approve incoming requests</li>
        <li><strong>Team Management</strong> — Team leads can manage their team roster</li>
      </ul>`
    ),
    createSection(
      'teams-tab',
      'Teams Tab',
      `<p>Browse all teams in the organization. Each team card shows:</p>
      <ul>
        <li>Team name and description</li>
        <li>Team lead</li>
        <li>Number of members</li>
      </ul>
      <p>Click a team to see its full member list and details. From here you can request to join if you're not already a member.</p>`
    ),
    createSection(
      'people-tab',
      'People Tab',
      '<p>View all employees with their current team assignments. Search by name or filter by department to find specific people. Click on a person to view their profile and team memberships.</p>'
    ),
    createSection(
      'join-requests',
      'Join Requests',
      `<p>The join request system enables flexible team membership:</p>
      <ul>
        <li><strong>Requesting to Join</strong> — Click "Request to Join" on any team card. The team lead receives a notification and can approve or decline.</li>
        <li><strong>Managing Requests</strong> — Team leads see pending join requests on their team page. Review and approve or decline them.</li>
        <li><strong>Leaving a Team</strong> — You can leave non-primary teams at any time from the team page.</li>
      </ul>`
    ),
    createSection(
      'team-creation',
      'Team Creation',
      '<p>New teams are typically created by admins or managers. Contact your manager or HR if you need a new team set up. Provide the proposed team name, description, purpose, and suggested team lead.</p>'
    ),
    createSection(
      'team-roles',
      'Team Roles',
      `<p>Within each team:</p>
      <ul>
        <li><strong>Team Lead</strong> — Manages team membership, approves join requests, and has visibility into team analytics and resource allocation</li>
        <li><strong>Member</strong> — Standard team membership with access to team communication and shared resources</li>
      </ul>`
    ),
  ],

  faqs: [
    createFAQ(
      'faq-create-team',
      'How do I create a new team?',
      'Team creation is managed by admins and managers. Contact your manager or HR with the proposed team name, purpose, and suggested lead.',
      ['create', 'new', 'team', 'how']
    ),
    createFAQ(
      'faq-join',
      'How do I join a team?',
      'Go to the Teams tab, find the team you want to join, and click "Request to Join". The team lead will review your request and approve or decline it.',
      ['join', 'request', 'membership', 'how']
    ),
    createFAQ(
      'faq-lead-responsibilities',
      'What are a team lead\'s responsibilities?',
      'Team leads manage team membership (approve/decline join requests), may have visibility into team analytics, and serve as the primary point of contact for their team.',
      ['lead', 'responsibilities', 'duties', 'role']
    ),
    createFAQ(
      'faq-leave-team',
      'Can I leave a team?',
      'You can leave non-primary teams at any time. Your primary team assignment is managed by HR/admin and typically corresponds to your department.',
      ['leave', 'quit', 'exit', 'remove']
    ),
    createFAQ(
      'faq-visibility',
      'What does team membership affect?',
      'Team membership affects which team analytics you see, resource allocation visibility, leave calendar views (you see teammates\' leave), and communication channels.',
      ['affect', 'visibility', 'what', 'impact']
    ),
  ],

  relatedLinks: [
    createRelatedLink('Directory', '/directory', { description: 'Search for people across the organization' }),
    createRelatedLink('Admin', '/admin', { description: 'User and team management (admins)' }),
    createRelatedLink('Resources', '/resources', { description: 'View team resource allocation' }),
  ],

  keywords: [
    'teams',
    'people',
    'membership',
    'join',
    'team lead',
    'organization',
    'groups',
    'roster',
    'structure',
  ],
});

// =============================================================================
// SECTION-LEVEL HELP
// =============================================================================

export const peopleTeamsJoinRequestsHelp: HelpContent = createHelpContent({
  id: 'people-teams:join-requests',
  title: 'Join Requests',
  description: 'Manage incoming team membership requests',
  parentId: 'people-teams',
  sections: [
    createSection(
      'overview',
      'Overview',
      '<p>As a team lead, review and manage join requests from employees who want to join your team. Approve or decline requests based on team capacity and the person\'s fit.</p>'
    ),
    createSection(
      'managing',
      'Managing Requests',
      `<p>For each pending request:</p>
      <ul>
        <li>Review the requester's profile and current team assignments</li>
        <li>Consider team capacity and skill needs</li>
        <li>Approve to add them to the team, or decline with an optional reason</li>
      </ul>`
    ),
  ],
  faqs: [
    createFAQ('faq-1', 'Can I invite someone to my team without a request?', 'Currently, the flow is request-based. Ask the person to submit a join request, then approve it. Admins can also directly assign team membership.', ['invite', 'add', 'direct']),
  ],
  keywords: ['join', 'requests', 'approve', 'decline', 'membership'],
});
